import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { LockKey, Scroll } from "@phosphor-icons/react";
import { useApi, errText } from "@/lib/api";
import { TERMS_VERSION, TermsContent } from "@/components/legal/TermsContent";
import { DeleteAccount } from "@/components/account/DataControls";

export const TermsGate = () => {
  const api = useApi();
  const qc = useQueryClient();
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const accept = async () => {
    setBusy(true);
    try { await api.post("/terms/accept", { version: TERMS_VERSION, accepted: true }); await qc.invalidateQueries(); }
    catch (e) { toast.error(errText(e, "Couldn't record your acceptance. Try again.")); }
    setBusy(false);
  };
  return (
    <div data-testid="terms-gate" className="max-w-3xl">
      <p className="font-mono text-xs text-[#A3A3A3] flex items-center gap-2"><Scroll className="text-[#00FF9D]" /> ONE LAST STEP</p>
      <h1 className="font-display text-3xl md:text-4xl font-semibold mt-1">Review and accept the Terms</h1>
      <div className="mt-6 max-h-[50vh] overflow-y-auto rounded-xl border border-[#2A2A2A] bg-[#0D0D0D] p-6"><TermsContent /></div>
      <label className="mt-6 flex items-start gap-3 text-sm cursor-pointer">
        <input data-testid="terms-agree-checkbox" type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-1 h-4 w-4 accent-[#00FF9D]" />
        <span>I have read and agree to the <Link to="/terms" target="_blank" className="text-[#00FF9D] underline">Terms of Service and Privacy Policy</Link>, including that CalibRunway is a tool and not financial advice, that I'm responsible for my company's finances, and the limits on liability for integrations and AI output.</span>
      </label>
      <button data-testid="terms-accept-btn" onClick={accept} disabled={!agree || busy}
        className="mt-5 rounded-full bg-[#00FF9D] px-6 py-3 font-semibold text-[#050505] disabled:opacity-40 hover:shadow-[0_0_24px_rgba(0,255,157,0.5)] transition-shadow">
        {busy ? "Saving…" : "Accept and continue"}
      </button>
      {!showDelete ? (
        <button data-testid="terms-decline-delete-link" onClick={() => setShowDelete(true)} className="mt-4 block text-xs text-[#6B6B6B] hover:text-[#FF8FA3] underline">Don't agree? Delete your account and data instead</button>
      ) : <div className="mt-6"><DeleteAccount /></div>}
    </div>
  );
};

export const TrialEnded = ({ plan, member }) => {
  const { pathname } = useLocation();
  if (pathname.startsWith("/app/billing") || pathname.startsWith("/app/account")) return null;
  if (member) return (
    <div data-testid="trial-ended" className="max-w-xl">
      <LockKey size={32} className="text-[#FFB800]" />
      <h1 className="font-display text-3xl font-semibold mt-3">This workspace is locked</h1>
      <p className="mt-3 text-[#A3A3A3]">The owner's trial or subscription has ended. Ask them to pick a plan to bring the team back in.</p>
    </div>
  );
  return (
    <div data-testid="trial-ended" className="max-w-xl">
      <LockKey size={32} className="text-[#FFB800]" />
      <h1 className="font-display text-3xl md:text-4xl font-semibold mt-3">{plan === "expired" ? "Your subscription has ended" : "Your 14-day free trial has ended"}</h1>
      <p className="mt-3 text-[#A3A3A3]">Your data is safe. Pick a plan to unlock your dashboard, the AI Scenario Calibrator, syncing and your scheduled emails.</p>
      <Link to="/app/billing" data-testid="trial-ended-cta" className="mt-6 inline-block rounded-full bg-[#00FF9D] px-6 py-3 font-semibold text-[#050505] hover:shadow-[0_0_24px_rgba(0,255,157,0.5)] transition-shadow">See plans</Link>
    </div>
  );
};
